import styled, { css } from 'styled-components'

/**
 * Tokens and text primitives the two finance tabs share. They live here
 * rather than in either tab so neither owns the other's palette.
 */

/**
 * The finance design system's type stack. Inter is not bundled with the app, so
 * it only resolves for users who have it installed; everyone else lands on the
 * platform UI font, which is what the design falls back to as well.
 */
export const FONT_STACK = `Inter, -apple-system, system-ui, 'Segoe UI', sans-serif`

/**
 * SureCo design-system tokens. Values mirror the semantic tokens published with
 * the finance design system (`--text-default`, `--bg-hover`, `--border-default`,
 * and the status families), so this page and the billing-profile page share one
 * palette.
 */
export const sureco = {
  /* Action */
  teal: '#01A0A8',
  tealDark: '#0A939B',
  focusRing: '#95F0F0',

  /* Surfaces */
  white: '#FFFFFF',
  pageBg: '#F7F9FC',
  offWhite: '#F0F4F8',

  /* Borders: soft for inner rules, strong for interactive controls */
  border: '#D9E2EC',
  borderSoft: '#F0F4F8',
  borderStrong: '#BCCCDC',

  /* Text */
  fg1: '#112A43',
  fg2: '#334E68',
  fg3: '#617D99',
  placeholder: '#839AB1',
  darkBlue: '#112A43',

  /* Status families: surface / border / text */
  successLight: '#F0FDF4',
  successBorder: '#BBF7D0',
  successText: '#4A6628',
  noticeLight: '#FEF9E7',
  noticeBorder: '#FDE396',
  noticeText: '#92400E',
  errorLight: '#FEE2E2',
  errorBorder: '#FCA5A5',
  errorText: '#7A2020',
  /* Destructive actions read brighter than error prose. */
  danger: '#B23F41',
  dangerDark: '#973638',
  /* Text on a teal fill. */
  onTeal: '#E9F1EF',

  shadowSm: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
} as const

/**
 * `link` buttons are inline actions rather than controls, so the boxed rules
 * below must skip them: a fill or a grey disabled box makes one read as a
 * different component from its own plain-text twin in the next row.
 */
const BOXED = ':not(.ant-btn-link)'

/**
 * The antd skin both finance pages wear. Applied to the page wrapper, so it
 * does not reach modal content: antd portals that outside the wrapper, and
 * each modal patches what it needs locally.
 */
export const financeControlSkin = css`
  /* antd sets its own family on these, so they need to be handed back. */
  &&& .ant-table-wrapper,
  &&& .ant-table,
  &&& .ant-btn,
  &&& .ant-input,
  &&& .ant-select {
    font-family: inherit;
  }

  /* Buttons — SureCo look & feel: 8px radius, no heavy shadow, medium weight */
  .ant-btn {
    border-radius: 8px;
    font-weight: 500;
    box-shadow: none;
  }
  .ant-btn-sm${BOXED} {
    height: 32px;
    font-size: 13px;
    padding: 0 12px;
  }

  /* Primary actions use SureCo teal */
  .ant-btn-primary {
    background: ${sureco.teal};
    border-color: ${sureco.teal};
  }
  .ant-btn-primary:not(:disabled):hover {
    background: ${sureco.tealDark} !important;
    border-color: ${sureco.tealDark} !important;
  }

  /* Secondary (default) buttons — neutral: white fill, dark text, subtle border */
  .ant-btn-default:not(:disabled):not(.ant-btn-dangerous) {
    background: ${sureco.white};
    border-color: ${sureco.border};
    color: ${sureco.fg1};
  }
  .ant-btn-default:not(:disabled):not(.ant-btn-dangerous):hover {
    background: ${sureco.offWhite} !important;
    border-color: ${sureco.fg3} !important;
    color: ${sureco.fg1} !important;
  }

  /* Disabled buttons — quiet, low-contrast, no muddy gray fill */
  .ant-btn:disabled${BOXED}, .ant-btn.ant-btn-disabled${BOXED} {
    background: ${sureco.offWhite} !important;
    border-color: ${sureco.borderSoft} !important;
    color: ${sureco.fg3} !important;
    box-shadow: none;
  }

  /* Danger buttons — outlined, fill on hover */
  .ant-btn-dangerous:not(:disabled)${BOXED} {
    background: ${sureco.white};
    border-color: ${sureco.danger};
    color: ${sureco.danger};
  }
  .ant-btn-dangerous:not(:disabled)${BOXED}:hover {
    background: ${sureco.danger} !important;
    border-color: ${sureco.danger} !important;
    color: ${sureco.white} !important;
  }

  /* Inline actions — plain text in the page palette, never a box */
  &&& .ant-btn-link {
    height: auto;
    padding: 0;
    border: none;
    background: transparent;
    box-shadow: none;
    font-size: 13px;
    font-weight: 500;
    color: ${sureco.teal};
  }
  &&& .ant-btn-link:not(:disabled):hover {
    color: ${sureco.tealDark};
  }
  &&& .ant-btn-link.ant-btn-dangerous {
    color: ${sureco.danger};
  }
  &&& .ant-btn-link.ant-btn-dangerous:not(:disabled):hover {
    color: ${sureco.dangerDark};
  }
  &&& .ant-btn-link:disabled,
  &&& .ant-btn-link.ant-btn-disabled {
    color: ${sureco.fg3};
  }

  /* Inputs/selects: 6px radius, stronger resting border, teal focus ring */
  .ant-input,
  .ant-input-affix-wrapper,
  .ant-select .ant-select-selector {
    border-radius: 6px;
    border-color: ${sureco.borderStrong};
    color: ${sureco.fg1};
  }
  .ant-input::placeholder,
  .ant-select-selection-placeholder {
    color: ${sureco.placeholder};
  }
  .ant-input-affix-wrapper:focus,
  .ant-input-affix-wrapper-focused,
  .ant-input:focus,
  .ant-select-focused .ant-select-selector {
    border-color: ${sureco.teal} !important;
    box-shadow: 0 0 0 3px ${sureco.focusRing} !important;
  }
`

/** An empty cell's dash, dimmed so it does not read as a value. */
export const NoneText = styled.span`
  font-size: 14px;
  color: ${sureco.fg3};
`

/** The asterisk on a required field's label. */
export const RequiredMark = styled.span`
  color: ${sureco.danger};
`

/** The numbers in "Showing 1-25 of 240". */
export const ShowingCount = styled.span`
  font-weight: 600;
  color: ${sureco.fg1};
`
